/**
 * Incremental per-source bootstrap for ZuneloProvider (SWR-friendly).
 * Applies each source as soon as it settles; peeks SPA cache first for instant paint.
 */

import { policyForSource, type SourceCacheSource } from '@zunelo/shared';
import { mapAgentSessionToAgentItem } from '../domain/mappers';
import { CONNECTABLE_SOURCES } from '../domain/providerIds';
import { applyAttentionSource, ATTENTION_SOURCE_LOADERS } from './sourceLoaders';
import type {
  AgentItem,
  AttentionItem,
  CurrentUserIdentity,
  DailyGroup,
  GmailMessage,
  GitHubPullRequest,
  HoldInfo,
  JiraIssue,
  NoteItem,
  ReminderItem,
  SentryOrExtSignal,
  SlackThread,
  AgentSession,
  StateSuggestion,
  WorkContextGraph,
  LlmWikiPage,
} from '../domain/types';

/**
 * Reconcile a bootstrap snapshot with whatever local state accumulated while
 * the fetch was in flight.
 *
 * The bootstrap fetch (`loadWorkspaceToolsBundle`) is kicked off on mount and
 * resolves asynchronously. If the user adds an item (optimistic prepend with
 * a locally-generated id) before that fetch settles, an ABSOLUTE
 * `setX(bundle.x)` would silently discard it — the bootstrap's snapshot was
 * captured before the addition happened, so it simply doesn't contain the
 * new id. Keep any current item whose id isn't in the bootstrap snapshot
 * (i.e. added locally after the fetch was already in flight) and otherwise
 * defer to the bootstrap's version, which is authoritative for anything it
 * already knows about.
 */
function reconcileBootstrapList<T extends { id: string }>(current: T[], bootstrap: T[]): T[] {
  const bootstrapIds = new Set(bootstrap.map((item) => item.id));
  const localOnly = current.filter((item) => !bootstrapIds.has(item.id));
  return [...localOnly, ...bootstrap];
}

/**
 * Same reconciliation as {@link reconcileBootstrapList}, but for daily groups:
 * the groups themselves are fixed ids ('ayer' | 'hoy' | 'bloq') always
 * present on both sides, so the race instead lives one level down, in each
 * group's `lines` (e.g. a custom line added via `addCustomDailyLine` while
 * the bootstrap fetch was in flight).
 */
function reconcileDailyGroups(current: DailyGroup[], bootstrap: DailyGroup[]): DailyGroup[] {
  return bootstrap.map((bootstrapGroup) => {
    const currentGroup = current.find((g) => g.id === bootstrapGroup.id);
    if (!currentGroup) return bootstrapGroup;
    return {
      ...bootstrapGroup,
      lines: reconcileBootstrapList(currentGroup.lines, bootstrapGroup.lines),
    };
  });
}
import { sourceLoadErrorMessage } from '../domain/sourceErrors';
import { toErrorMessage } from '../services/errors';
import {
  agentsClient,
  migrateLocalWorkspaceToolsIfNeeded,
  shouldUseMockWorkspace,
  workspaceClient,
} from '../services/clients';
import { getDefaultSourceCache } from '../services/cache/sourceCache';
import { sourceCacheStorageKey } from '../services/cache/types';
import { withForcedListReads } from '../services/cache/wrapListClient';

export type PrTalkMap = Record<
  string,
  Array<{ ch: string; text: string; when: string; id: string }>
>;

export type BootstrapDeps = {
  currentUser?: CurrentUserIdentity;
  itemsRef: { current: AttentionItem[] };
  setItems: (updater: (prev: AttentionItem[]) => AttentionItem[]) => void;
  setRawJiraIssues: (value: JiraIssue[]) => void;
  setRawPullRequests: (value: GitHubPullRequest[]) => void;
  setRawSlackThreads: (value: SlackThread[]) => void;
  setRawAgentSessions: (value: AgentSession[]) => void;
  setAgents: (value: AgentItem[]) => void;
  setRawGmailMessages: (value: GmailMessage[]) => void;
  // Accept an updater form too: the bootstrap settle handler must reconcile
  // against whatever local state accumulated while the fetch was in flight
  // (see reconcileBootstrapList) rather than blindly overwriting it.
  setReminders: (value: ReminderItem[] | ((prev: ReminderItem[]) => ReminderItem[])) => void;
  setNotes: (value: NoteItem[] | ((prev: NoteItem[]) => NoteItem[])) => void;
  setLlmWiki?: (value: LlmWikiPage[] | ((prev: LlmWikiPage[]) => LlmWikiPage[])) => void;
  setDailyGroups: (value: DailyGroup[] | ((prev: DailyGroup[]) => DailyGroup[])) => void;
  setDailySent: (value: boolean) => void;
  setHoldData: (value: Record<string, HoldInfo>) => void;
  setSuggestData: (value: Record<string, StateSuggestion>) => void;
  setExtSignals: (value: Record<string, SentryOrExtSignal[]>) => void;
  setPrTalkMap: (value: PrTalkMap) => void;
  setPreviewOverride: (value: string | null) => void;
  setWorkContextGraph: (value: WorkContextGraph) => void;
  setErrors: (
    updater: (prev: Record<string, string | null>) => Record<string, string | null>,
  ) => void;
  setLoadingSources: (
    value: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  setIsLoading: (value: boolean) => void;
  isMounted: () => boolean;
  dataEpoch: number;
  getDataEpoch: () => number;
};

function markSourceDone(deps: BootstrapDeps, source: string): void {
  deps.setLoadingSources((prev) => ({ ...prev, [source]: false }));
}

/** Applies one connectable-source fetch result via its ATTENTION_SOURCE_LOADERS entry and marks it done. */
function applyProviderSource(
  deps: BootstrapDeps,
  source: (typeof CONNECTABLE_SOURCES)[number],
  raw: unknown[],
): void {
  applyAttentionSource(deps, source, raw);
  markSourceDone(deps, source);
}

function applyAgents(deps: BootstrapDeps, sessions: AgentSession[]): void {
  deps.setRawAgentSessions(sessions);
  deps.setAgents(sessions.map(mapAgentSessionToAgentItem));
  deps.setErrors((prev) => ({ ...prev, agents: null }));
  markSourceDone(deps, 'agents');
}

function peekSourceList<T>(
  uid: string | undefined,
  source: SourceCacheSource,
  listKey = 'list',
): T | null {
  const cache = getDefaultSourceCache();
  const peeked = cache.peek<T>(
    sourceCacheStorageKey(uid, source, listKey),
    source,
    policyForSource(source),
  );
  return peeked?.entry.data ?? null;
}

/** Apply durable/memory snapshots so Daily/Radar can paint before network settles. */
export function applyCachedSnapshots(deps: BootstrapDeps): boolean {
  const uid = deps.currentUser?.uid;
  let any = false;

  for (const source of CONNECTABLE_SOURCES) {
    const raw = peekSourceList<unknown[]>(uid, source);
    if (raw) {
      applyProviderSource(deps, source, raw);
      any = true;
    }
  }
  const agents = peekSourceList<AgentSession[]>(uid, 'agents');
  if (agents) {
    applyAgents(deps, agents);
    any = true;
  }
  const reminders = peekSourceList<ReminderItem[]>(uid, 'workspace', 'reminders');
  if (reminders) {
    deps.setReminders(reminders);
    any = true;
  }
  const notes = peekSourceList<NoteItem[]>(uid, 'workspace', 'notes');
  if (notes) {
    deps.setNotes(notes);
    any = true;
  }
  const llmWiki = peekSourceList<LlmWikiPage[]>(uid, 'workspace', 'llmWiki');
  if (llmWiki) {
    deps.setLlmWiki?.(llmWiki);
    any = true;
  }
  const daily = peekSourceList<DailyGroup[]>(uid, 'workspace', 'dailyGroups');
  if (daily) {
    deps.setDailyGroups(daily);
    any = true;
  }
  const hold = peekSourceList<Record<string, HoldInfo>>(uid, 'workspace', 'holdData');
  if (hold) {
    deps.setHoldData(hold);
    any = true;
  }
  const suggest = peekSourceList<Record<string, StateSuggestion>>(uid, 'workspace', 'suggestions');
  if (suggest) {
    deps.setSuggestData(suggest);
    any = true;
  }
  const ext = peekSourceList<Record<string, SentryOrExtSignal[]>>(
    uid,
    'workspace',
    'externalSignals',
  );
  if (ext) {
    deps.setExtSignals(ext);
    any = true;
  }
  const prTalk = peekSourceList<PrTalkMap>(uid, 'workspace', 'prTalkMap');
  if (prTalk) {
    deps.setPrTalkMap(prTalk);
    any = true;
  }

  if (any) {
    deps.setIsLoading(false);
  }
  return any;
}

async function settle<T>(
  deps: BootstrapDeps,
  source: string,
  promise: Promise<T>,
  onOk: (value: T) => void,
  onErr: (reason: unknown) => void,
): Promise<void> {
  try {
    const value = await promise;
    if (!deps.isMounted()) return;
    if (deps.dataEpoch !== deps.getDataEpoch() && deps.itemsRef.current.length > 0) return;
    onOk(value);
  } catch (reason) {
    if (!deps.isMounted()) return;
    onErr(reason);
    markSourceDone(deps, source);
  }
}

type WorkspaceToolsBundle = {
  reminders: ReminderItem[];
  notes: NoteItem[];
  llmWiki: LlmWikiPage[];
  dailyGroups: DailyGroup[];
  holdData: Record<string, HoldInfo>;
  suggestions: Record<string, StateSuggestion>;
  externalSignals: Record<string, SentryOrExtSignal[]>;
  prTalkMap: PrTalkMap;
  dailyDraft: string | null;
  dailySent: boolean;
};

/**
 * Emits a single structured, safe-to-log diagnostic line for a workspace
 * bootstrap read/migration step that failed and is being tolerated (the UX
 * falls back gracefully rather than bricking the rest of the bootstrap).
 *
 * Carries only a stable `step` label and an error classification —
 * `name`/`status` when available, never `error.message` or any response
 * body — so this can never leak user content, credentials, or tokens that
 * might incidentally appear in an error message (e.g. a URL with a query
 * param, or a server error echoing part of the request).
 */
function emitBootstrapDiagnostic(step: string, err: unknown): void {
  const status =
    err && typeof err === 'object' && 'status' in err
      ? (err as { status?: unknown }).status
      : undefined;
  const name = err instanceof Error ? err.name : typeof err;
  console.warn(
    JSON.stringify({
      event: 'workspace_bootstrap_degraded',
      step,
      errorName: name,
      ...(typeof status === 'number' ? { status } : {}),
    }),
  );
}

/** True when the thrown client error signals the endpoint is not deployed yet (deploy skew). */
function isNotFoundError(err: unknown): boolean {
  return Boolean(
    err &&
    typeof err === 'object' &&
    'status' in err &&
    (err as { status?: unknown }).status === 404,
  );
}

/**
 * True when the server explicitly reports a route as not implemented yet
 * (e.g. `hold-data` / `suggestions` / `external-signals` / `pr-talk-map`,
 * which are still placeholders on the backend). Bootstrap treats this as
 * "no data available" rather than a hard failure — see
 * `openspec/changes/daily-slack-send/proposal.md`.
 */
function isNotImplementedError(err: unknown): boolean {
  return Boolean(
    err &&
    typeof err === 'object' &&
    'status' in err &&
    (err as { status?: unknown }).status === 501,
  );
}

type WorkspaceToolsCore = {
  reminders: ReminderItem[];
  notes: NoteItem[];
  llmWiki: LlmWikiPage[];
  wikiReadOk: boolean;
  dailyGroups: DailyGroup[];
  dailyDraft: string | null;
  dailySent: boolean;
  draftStatusOk: boolean;
};

/**
 * Fetch reminders/notes/wiki/daily/draft/status individually. This is the
 * legacy path, kept as a fallback for deploy skew (bootstrap endpoint 404).
 */
async function loadWorkspaceToolsCoreLegacy(): Promise<WorkspaceToolsCore> {
  const [rem, notes, daily] = await Promise.all([
    workspaceClient.getReminders(),
    workspaceClient.getNotes(),
    workspaceClient.getDailyGroups(),
  ]);

  const reminders = rem.data;
  const notesData = notes.data;
  let llmWiki: LlmWikiPage[] = [];
  let wikiReadOk = false;
  const dailyGroups = daily.data;
  let dailyDraft: string | null = null;
  let dailySent = false;
  let draftStatusOk = false;

  try {
    const wikiRes = await workspaceClient.getLlmWiki();
    llmWiki = wikiRes.data;
    wikiReadOk = true;
  } catch (err) {
    // Best effort: tolerate a transient 404/500 (e.g. deploy skew) without
    // bricking the rest of the workspace load. Wiki state is UNKNOWN.
    emitBootstrapDiagnostic('llmWiki_legacy_read', err);
  }

  try {
    const [draftRes, statusRes] = await Promise.all([
      workspaceClient.getDailyDraft(),
      workspaceClient.getDailyStatus(),
    ]);
    dailyDraft = draftRes.data;
    dailySent = statusRes.data.sent;
    draftStatusOk = true;
  } catch (err) {
    // Optional draft / sent status
    emitBootstrapDiagnostic('daily_draft_status_legacy_read', err);
  }

  return {
    reminders,
    notes: notesData,
    llmWiki,
    wikiReadOk,
    dailyGroups,
    dailyDraft,
    dailySent,
    draftStatusOk,
  };
}

/**
 * Fetch reminders/notes/wiki/daily/draft/status via the aggregated bootstrap
 * endpoint (single request). Throws when the endpoint is unavailable so the
 * caller can fall back to the legacy per-list path.
 */
async function loadWorkspaceToolsCoreViaBundle(): Promise<WorkspaceToolsCore> {
  const bundleRes = await workspaceClient.getWorkspaceBundle();
  const bundle = bundleRes.data;
  return {
    reminders: bundle.reminders,
    notes: bundle.notes,
    llmWiki: bundle.llmWiki,
    wikiReadOk: bundle.sections.llmWiki.status === 'ok',
    dailyGroups: bundle.dailyGroups,
    dailyDraft: bundle.dailyDraft,
    dailySent: bundle.dailySent.sent,
    draftStatusOk: true,
  };
}

/**
 * Load workspace lists + daily draft/status, then one-shot migrate local mock
 * data into the cloud document when live workspace is enabled.
 *
 * Prefers the aggregated `getWorkspaceBundle()` endpoint (one HTTP call
 * instead of five); falls back to the legacy per-list reads when the
 * endpoint 404s (deploy skew — the endpoint not yet deployed).
 */
async function loadWorkspaceToolsBundle(): Promise<WorkspaceToolsBundle> {
  const [core, hold, suggest, ext, prTalk] = await Promise.all([
    loadWorkspaceToolsCoreViaBundle().catch((err) => {
      if (!isNotFoundError(err)) throw err;
      return loadWorkspaceToolsCoreLegacy();
    }),
    workspaceClient.getHoldData().catch((err) => {
      if (!isNotImplementedError(err)) throw err;
      return { data: {} as Record<string, HoldInfo>, serverTime: new Date().toISOString() };
    }),
    workspaceClient.getSuggestions().catch((err) => {
      if (!isNotImplementedError(err)) throw err;
      return { data: {} as Record<string, StateSuggestion>, serverTime: new Date().toISOString() };
    }),
    workspaceClient.getExternalSignals().catch((err) => {
      if (!isNotImplementedError(err)) throw err;
      return {
        data: {} as Record<string, SentryOrExtSignal[]>,
        serverTime: new Date().toISOString(),
      };
    }),
    workspaceClient.getPrTalkMap().catch((err) => {
      if (!isNotImplementedError(err)) throw err;
      return { data: {} as PrTalkMap, serverTime: new Date().toISOString() };
    }),
  ]);

  let reminders = core.reminders;
  let notesData = core.notes;
  let llmWiki = core.llmWiki;
  const wikiReadOk = core.wikiReadOk;
  let dailyGroups = core.dailyGroups;
  let dailyDraft = core.dailyDraft;
  const dailySent = core.dailySent;
  const draftStatusOk = core.draftStatusOk;

  if (draftStatusOk && !shouldUseMockWorkspace()) {
    try {
      const migrated = await migrateLocalWorkspaceToolsIfNeeded(workspaceClient, {
        reminders,
        notes: notesData,
        dailyGroups,
        dailyDraft,
        dailySent,
        llmWiki: wikiReadOk ? llmWiki : null,
      });
      if (migrated) {
        reminders = migrated.reminders;
        notesData = migrated.notes;
        dailyGroups = migrated.dailyGroups;
        dailyDraft = migrated.dailyDraft;
        llmWiki = migrated.llmWiki;
      }
    } catch (err) {
      // Best effort migration
      emitBootstrapDiagnostic('local_workspace_migration', err);
    }
  }

  return {
    reminders,
    notes: notesData,
    llmWiki,
    dailyGroups,
    holdData: hold.data,
    suggestions: suggest.data,
    externalSignals: ext.data,
    prTalkMap: prTalk.data,
    dailyDraft,
    dailySent,
  };
}

export async function runIncrementalBootstrap(deps: BootstrapDeps): Promise<void> {
  deps.setIsLoading(true);
  deps.setErrors((prev) => ({ ...prev, global: null }));
  deps.setLoadingSources({
    jira: true,
    slack: true,
    github: true,
    gmail: true,
    agents: true,
    workspace: true,
  });

  applyCachedSnapshots(deps);

  await withForcedListReads(async () => {
    const tasks: Promise<void>[] = [
      ...CONNECTABLE_SOURCES.map((source) =>
        settle(
          deps,
          source,
          ATTENTION_SOURCE_LOADERS[source].fetch(),
          (res) => applyProviderSource(deps, source, res.data),
          (reason) =>
            deps.setErrors((prev) => ({
              ...prev,
              [source]: sourceLoadErrorMessage(source, reason),
            })),
        ),
      ),
      settle(
        deps,
        'agents',
        agentsClient.getSessions(),
        (res) => applyAgents(deps, res.data),
        () =>
          deps.setErrors((prev) => ({
            ...prev,
            agents: 'Error al conectar con los agentes autónomos.',
          })),
      ),
      settle(
        deps,
        'workspace',
        Promise.all([loadWorkspaceToolsBundle(), workspaceClient.getWorkContextGraph()]),
        ([bundle, workCtx]) => {
          // Reconcile rather than overwrite: a user can add/edit a reminder,
          // note, wiki page, or daily line optimistically after this fetch
          // started but before it resolved. See reconcileBootstrapList /
          // reconcileDailyGroups above for why an absolute `setX(bundle.x)`
          // would silently drop that mutation.
          deps.setReminders((prev) => reconcileBootstrapList(prev, bundle.reminders));
          deps.setNotes((prev) => reconcileBootstrapList(prev, bundle.notes));
          deps.setLlmWiki?.((prev) => reconcileBootstrapList(prev, bundle.llmWiki));
          deps.setDailyGroups((prev) => reconcileDailyGroups(prev, bundle.dailyGroups));
          deps.setHoldData(bundle.holdData);
          deps.setSuggestData(bundle.suggestions);
          deps.setExtSignals(bundle.externalSignals);
          deps.setPrTalkMap(bundle.prTalkMap);
          deps.setDailySent(bundle.dailySent);
          if (bundle.dailyDraft) deps.setPreviewOverride(bundle.dailyDraft);
          deps.setWorkContextGraph(workCtx.data);
          deps.setErrors((prev) => ({ ...prev, workspace: null }));
          markSourceDone(deps, 'workspace');
        },
        (reason) =>
          deps.setErrors((prev) => ({
            ...prev,
            workspace: toErrorMessage(reason, 'Error al cargar herramientas del workspace.'),
          })),
      ),
    ];

    await Promise.allSettled(tasks);
  });
  if (!deps.isMounted()) return;

  deps.setIsLoading(false);
  deps.setLoadingSources({
    jira: false,
    slack: false,
    github: false,
    gmail: false,
    agents: false,
    workspace: false,
  });
}
